'use client';

import { useCallback, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import { Download, Printer, QrCode, Sticker } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { useSingleQrPrint } from '@/features/configurations';
import { ShipmentPrintReceipt } from './shipment-print-receipt';
import type { Shipment } from '../types';

interface ShipmentQrDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shipmentNumber: string;
  qrCode: string;
  shipment?: Shipment;
}

export function ShipmentQrDialog({
  open,
  onOpenChange,
  shipmentNumber,
  qrCode,
  shipment
}: ShipmentQrDialogProps) {
  const t = useTranslations('shipping');
  const qrRef = useRef<HTMLDivElement>(null);
  const [printMode, setPrintMode] = useState(false);
  const [stickerPrinting, setStickerPrinting] = useState(false);
  const { printerUid, printSingleQr } = useSingleQrPrint();

  const handlePrintSticker = useCallback(async () => {
    if (!printerUid) {
      toast.message(t('setDefaultPrinter'));
      return;
    }
    setStickerPrinting(true);
    const result = await printSingleQr({
      headerText: shipmentNumber,
      qrPayload: qrCode
    });
    setStickerPrinting(false);
    if (result.ok) {
      toast.success(t('printSentToZebra'));
    } else {
      toast.error(t('printFailedZebra', { reason: result.error }));
    }
  }, [printerUid, printSingleQr, shipmentNumber, qrCode, t]);

  const handlePrint = useCallback(() => {
    setPrintMode(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
        setPrintMode(false);
      });
    });
  }, []);

  const handleDownloadQr = useCallback(() => {
    if (!qrRef.current) return;
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();

    img.onload = async () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const imgData = canvas.toDataURL('image/png');

      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const qrSize = 80;
      const x = (pdfWidth - qrSize) / 2;
      const y = (pdfHeight - qrSize) / 2 - 10;

      pdf.addImage(imgData, 'PNG', x, y, qrSize, qrSize);
      pdf.setFontSize(14);
      pdf.text(shipmentNumber, pdfWidth / 2, y + qrSize + 10, {
        align: 'center'
      });

      pdf.save(`${shipmentNumber}-qr.pdf`);
    };

    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
  }, [shipmentNumber]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader className='items-center gap-2'>
            <div className='flex items-center gap-3'>
              <DialogTitle>{t('manifestQrTitle')}</DialogTitle>
              <QrCode className='text-primary h-6 w-6' />
            </div>
            <p className='text-muted-foreground text-sm'>
              {t('manifestQrSubtitle')}
            </p>
          </DialogHeader>

          <div className='flex flex-col items-center space-y-4'>
            <p className='text-muted-foreground text-sm'>
              {t('manifestNumber')}
            </p>
            <p className='text-lg font-bold'>{shipmentNumber}</p>

            <div ref={qrRef} className='rounded-xl bg-amber-50 p-6'>
              <QRCode value={qrCode} size={200} level='M' />
            </div>

            <Button className='w-full' onClick={handleDownloadQr}>
              <Download className='me-2 h-4 w-4' />
              {t('downloadHighRes')}
            </Button>

            <Button
              variant='outline'
              className='w-full'
              onClick={handlePrintSticker}
              disabled={stickerPrinting || !printerUid}
            >
              <Sticker className='me-2 h-4 w-4' />
              {stickerPrinting ? t('printing') : t('printSticker')}
            </Button>

            {shipment && shipment.boxes && shipment.boxes.length > 0 && (
              <Button
                variant='outline'
                className='w-full'
                onClick={handlePrint}
              >
                <Printer className='me-2 h-4 w-4' />
                {t('printForm')}
              </Button>
            )}

            <button
              type='button'
              className='text-muted-foreground hover:text-foreground text-sm transition-colors'
              onClick={() => onOpenChange(false)}
            >
              {t('close')}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {shipment && (
        <ShipmentPrintReceipt shipment={shipment} visible={printMode} />
      )}
    </>
  );
}
